import type { TemplateFormSubmitValuesProps, TemplateMetadata } from '~/components/GenericTemplates/types';
import type { MoiEmailTemplatesResponse } from '~/server_shared/openapi/data-contracts';

// Source the wire types from the generated OpenAPI contracts (single source of truth with the backend).
// A MOI email template as returned by the list endpoint; `id` is null while the Default is still
// synthetic (not yet materialized) and `is_default` identifies the Default row.
export type MoiEmailTemplate = MoiEmailTemplatesResponse['templates'][number];
// A MOI method, identified by its org-stable key with a display name resolved for the UI.
export type MoiMethod = NonNullable<MoiEmailTemplate['moi_methods']>[number];

export type { MoiEmailTemplatesResponse, MoiMethodsResponse } from '~/server_shared/openapi/data-contracts';

// Form values for the MOI email editor: the shared template fields plus the method-key mapping and
// the attached document template (same field the email templates carry).
// `id` is nullable so save can dispatch POST (materialize) when null and PUT when it is an integer.
export interface MoiEmailTemplateFormValues extends Omit<TemplateFormSubmitValuesProps, 'id'> {
  id?: number | null;
  is_default?: boolean;
  applies_to_moi_method_keys: string[];
  document_template_id?: number | null;
}

// The synthetic Default has no id; render it and select it via this sentinel so row identity is
// stable in the table (SortableTable keys/selection are id-based).
export const MOI_DEFAULT_ROW_ID = -1;

export const moiEmailTemplateToRowId = (template: MoiEmailTemplate): number => template.id ?? MOI_DEFAULT_ROW_ID;

// When the synthetic Default is materialized, its row id changes from the MOI_DEFAULT_ROW_ID sentinel
// to a real (>0) id. If the Default was the selected row (via the sentinel), return the materialized
// Default's real id so the selection can follow it; otherwise return null (no remap needed).
export const resolveMaterializedDefaultRowId = (
  selectedRowId: number,
  templates: MoiEmailTemplate[]
): number | null => {
  if (selectedRowId !== MOI_DEFAULT_ROW_ID) return null;
  const defaultRow = templates.find((template) => template.is_default);
  if (!!defaultRow?.id && defaultRow.id > 0) return defaultRow.id;
  return null;
};

// Table rows reuse the shared TemplateMetadata shape so the shared column primitives render; MOI
// adds the resolved methods + the default flag for the badge/lock and the "ALL" methods display.
// `id` carries the row identity (MOI_DEFAULT_ROW_ID for the not-yet-materialized synthetic Default).
export interface MoiEmailTemplateMetadata extends TemplateMetadata {
  is_default: boolean;
  is_all_lobs?: boolean;
  moi_methods: MoiMethod[];
}

export const moiEmailTemplateToMetadata = (template: MoiEmailTemplate): MoiEmailTemplateMetadata => ({
  id: moiEmailTemplateToRowId(template),
  template_name: template.template_name ?? '',
  template_type: 'moi_email',
  last_update_datetime: template.last_update_datetime ? new Date(template.last_update_datetime) : new Date(0),
  sub_organization_ids: template.sub_organization_ids ?? [],
  lobs: template.lobs ?? [],
  is_all_lobs: template.is_all_lobs ?? undefined,
  countries: template.countries ?? undefined,
  is_all_countries: template.is_all_countries ?? undefined,
  states: template.states ?? undefined,
  coverage_keys: template.coverage_keys ?? undefined,
  is_claim_level: template.is_claim_level,
  is_enabled: template.is_enabled,
  dynamic_token_fields: template.dynamic_token_fields ?? [],
  connected_automatic_rule_actions_display_names: template.connected_automatic_rule_actions_display_names ?? undefined,
  is_all_coverages: template.is_all_coverages ?? undefined,
  is_default: !!template.is_default,
  moi_methods: template.moi_methods ?? [],
});

export const moiEmailTemplateToFormValues = (template: MoiEmailTemplate): MoiEmailTemplateFormValues => {
  // The Default's scope is locked to "ALL" — force every is_all_* flag so a wire payload that omits
  // one can't surface a non-ALL scope for it.
  const isDefault = !!template.is_default;
  return {
    id: template.id ?? null,
    is_default: isDefault,
    is_all_suborgs: isDefault || (template.is_all_suborgs ?? false),
    sub_organization_ids: template.sub_organization_ids ?? [],
    is_all_lobs: isDefault || (template.is_all_lobs ?? true),
    lobs: template.lobs ?? [],
    coverage_keys: template.coverage_keys ?? [],
    is_all_coverages: isDefault || (template.is_all_coverages ?? true),
    countries: template.countries ?? undefined,
    is_all_countries: isDefault || (template.is_all_countries ?? false),
    states: template.states ?? [],
    is_all_states: isDefault || (template.is_all_states ?? true),
    is_enabled: template.is_enabled,
    template_name: template.template_name ?? '',
    template_type: 'moi_email',
    // MOI email is Exposure-level only.
    is_claim_level: false,
    body_template: template.body_template,
    title_template: template.title_template ?? '',
    dynamic_token_fields: template.dynamic_token_fields ?? [],
    applies_to_moi_method_keys: (template.moi_methods ?? []).map((method) => method.key),
    document_template_id: template.document_template_id ?? null,
  };
};
